import { Controller } from "@hotwired/stimulus"

/**
 * Document Pages controller
 * Manages document pages with 3D page turning animations
 * Handles both PDFs (multi-page) and images (single page)
 * Also provides version comparison with interactive slider
 */
export default class extends Controller {
  static targets = [
    "page", 
    "indicator", 
    "pageCounter", 
    "currentPageNum",
    "prevButton",
    "nextButton",
    "versionSelector",
    "versionImage",
    "compareSwitch",
    "previewContainer",
    "regularView",
    "comparisonView",
    "comparisonPage",
    "compareSlider",
    "compareVersionContainer",
    "sliderHandle",
    "compareOriginal",
    "compareVersion",
    "elaSwitch",
    "elaOverlay",
    "copyMoveSwitch",
    "copyMoveOverlay",
    "doctamperSwitch",
    "doctamperOverlay",
    "doctamperHeatmapSwitch",
    "doctamperHeatmapOverlay",
    "pdfAnnotationsSwitch",
    "pdfAnnotationsOverlay",
    "tooltipOverlay",
    "jsonSwitch",
    "jsonView",
    "copyJsonButton",
    "blackWhiteSwitch",
    "invertColorSwitch"
  ]
  
  static values = {
    imageManipulation: String,
    resurrectData: String
  }
  

  connect() {
    this.currentPage = 0
    this.totalPages = this.pageTargets.length
    this.isDragging = false
    
    // Create shared tooltip in overlay
    if (this.hasTooltipOverlayTarget) {
      this.sharedTooltip = this.createSharedTooltip()
      this.tooltipOverlayTarget.appendChild(this.sharedTooltip)
    }
    
    // Check if this is a PDF (has navigation) or an image (no navigation)
    const hasPagination = this.hasPrevButtonTarget && this.hasNextButtonTarget
    
    // For PDFs with navigation
    if (hasPagination) {
      if (this.totalPages <= 1) {
        this.prevButtonTarget.disabled = true
        this.nextButtonTarget.disabled = true
      }
    }
    
    // Auto-activate doctamper bounding boxes if available
    if (this.hasImageManipulationValue && this.imageManipulationValue && this.hasDoctamperSwitchTarget) {
      try {
        const data = JSON.parse(this.imageManipulationValue)
        const boundingBoxes = data?.detections?.doctamper?.bounding_boxes
        
        if (boundingBoxes && Array.isArray(boundingBoxes) && boundingBoxes.length > 0) {
          // Check the doctamper switch and trigger the toggle
          this.doctamperSwitchTarget.checked = true
          this.toggleDoctamperMode({ target: this.doctamperSwitchTarget })
        }
      } catch (error) {
        console.error('Error checking for doctamper bounding boxes:', error)
      }
    }
    
    // Auto-activate PDF annotations if available
    if (this.hasResurrectDataValue && this.resurrectDataValue && this.hasPdfAnnotationsSwitchTarget) {
      try {
        const data = JSON.parse(this.resurrectDataValue)
        // Use filtered annotations that exclude hidden edits
        const annotations = data?.pdf_annotations?.annotations_for_display || data?.pdf_annotations?.annotations
        
        if (annotations && Array.isArray(annotations) && annotations.length > 0) {
          // Check the PDF annotations switch and trigger the toggle
          this.pdfAnnotationsSwitchTarget.checked = true
          this.togglePdfAnnotationsMode({ target: this.pdfAnnotationsSwitchTarget })
        }
      } catch (error) {
        console.error('Error checking for PDF annotations:', error)
      }
    }
    
    // Initialize slider events if we have the compare elements
    if (this.hasSliderHandleTarget) {
      // Add document-level event listeners for slider dragging
      document.addEventListener('mousemove', this.handleDrag.bind(this))
      document.addEventListener('mouseup', this.stopDrag.bind(this))
      document.addEventListener('touchmove', this.handleDrag.bind(this))
      document.addEventListener('touchend', this.stopDrag.bind(this))
      
      // Initialize all slider image positions to ensure proper sizing on page load
      this.initializeSliderImages()
    }
    
    // Update current page styling
    this.updatePageDisplay()
  }
  
  // Initialize all slider images for proper sizing on page load
  initializeSliderImages() {
    // Find all comparison sliders
    const sliders = this.element.querySelectorAll('[data-document-pages-target="compareSlider"]')
    sliders.forEach(slider => {
      const versionContainer = slider.querySelector('[data-document-pages-target="compareVersionContainer"]')
      const versionImage = versionContainer ? versionContainer.querySelector('img') : null
      const originalContainer = slider.querySelector('.document-app__compare-original')
      const originalImage = originalContainer ? originalContainer.querySelector('img') : null
      
      if (versionImage) {
        // Set initial styles for version image
        versionImage.style.position = 'absolute'
        versionImage.style.top = '0'
        versionImage.style.left = '0'
        versionImage.style.width = `${slider.offsetWidth}px`
        versionImage.style.height = '100%'
        versionImage.style.maxWidth = 'none'
        versionImage.style.objectFit = 'contain'
        versionImage.style.objectPosition = 'center'
      }
      
      if (originalImage) {
        // Set initial styles for original image
        originalImage.style.position = 'absolute'
        originalImage.style.top = '0'
        originalImage.style.left = '0'
        originalImage.style.width = '100%'
        originalImage.style.height = '100%'
        originalImage.style.objectFit = 'contain'
        originalImage.style.objectPosition = 'center'
      }
    })
  }
  

  disconnect() {
    // Clean up event listeners when controller is disconnected
    document.removeEventListener('mousemove', this.handleDrag.bind(this))
    document.removeEventListener('mouseup', this.stopDrag.bind(this))
    document.removeEventListener('touchmove', this.handleDrag.bind(this))
    document.removeEventListener('touchend', this.stopDrag.bind(this))
    
    // Clean up shared tooltip
    if (this.sharedTooltip && this.hasTooltipOverlayTarget) {
      this.tooltipOverlayTarget.removeChild(this.sharedTooltip)
      this.sharedTooltip = null
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++
      this.updatePageDisplay()
      
      // Also update comparison view if we're in compare mode
      if (this.hasCompareSwitchTarget && this.compareSwitchTarget.checked) {
        this.updateComparisonPages()
      }
    }
  }

  prevPage() {
    if (this.currentPage > 0) {
      this.currentPage--
      this.updatePageDisplay()
      
      // Also update comparison view if we're in compare mode
      if (this.hasCompareSwitchTarget && this.compareSwitchTarget.checked) {
        this.updateComparisonPages()
      }
    }
  }
  
  goToPage(event) {
    const pageIndex = parseInt(event.currentTarget.dataset.page)
    if (pageIndex >= 0 && pageIndex < this.totalPages) {
      this.currentPage = pageIndex
      this.updatePageDisplay()
      
      // Also update comparison view if we're in compare mode
      if (this.hasCompareSwitchTarget && this.compareSwitchTarget.checked) {
        this.updateComparisonPages()
      }
    }
  }

  // Toggle between standard view and comparison view
  toggleCompareMode(event) {
    const isCompareMode = event.target.checked
    
    if (isCompareMode) {
      // Uncheck other modes
      if (this.hasElaSwitchTarget) this.elaSwitchTarget.checked = false
      if (this.hasCopyMoveSwitchTarget) this.copyMoveSwitchTarget.checked = false
      
      // Uncheck and disable filter modes
      if (this.hasBlackWhiteSwitchTarget) {
        this.blackWhiteSwitchTarget.checked = false
        this.removeBlackWhiteFilter()
      }
      if (this.hasInvertColorSwitchTarget) {
        this.invertColorSwitchTarget.checked = false
        this.removeInvertColorFilter()
      }
      
      // Hide manipulation overlays
      if (this.hasElaOverlayTarget) {
        this.elaOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      if (this.hasCopyMoveOverlayTarget) {
        this.copyMoveOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      
      // Hide filter section
      const filterSection = this.element.querySelector('.document-app__filter-section')
      if (filterSection) filterSection.classList.add('d-none')
      
      // Hide zoom section
      const zoomSection = this.element.querySelector('.document-app__zoom-section')
      if (zoomSection) zoomSection.classList.add('d-none')
      
      // Hide all views
      this.hideAllViews()
      
      // Show comparison view
      if (this.hasComparisonViewTarget) this.comparisonViewTarget.classList.remove('d-none')
      
      // Update comparison page visibility to show current page
      this.updateComparisonPages()
      
      // Reset slider position to 50% for consistent experience
      if (this.hasComparisonPageTarget) {
        this.comparisonPageTargets.forEach(page => {
          const sliderHandle = page.querySelector('[data-document-pages-target="sliderHandle"]')
          const versionContainer = page.querySelector('[data-document-pages-target="compareVersionContainer"]')
          
          if (sliderHandle && versionContainer) {
            sliderHandle.style.left = '50%'
            versionContainer.style.width = '50%'
          }
        })
      }
      
      // Initialize image sizes when entering comparison mode
      // Do this after a slight delay to ensure the view is rendered
      setTimeout(() => this.initializeSliderImages(), 50)
    } else {
      // Show regular view, hide comparison view
      if (this.hasRegularViewTarget) this.regularViewTarget.classList.remove('d-none')
      if (this.hasComparisonViewTarget) this.comparisonViewTarget.classList.add('d-none')
      
      // Show filter section
      const filterSection = this.element.querySelector('.document-app__filter-section')
      if (filterSection) filterSection.classList.remove('d-none')
      
      // Show zoom section
      const zoomSection = this.element.querySelector('.document-app__zoom-section')
      if (zoomSection) zoomSection.classList.remove('d-none')
    }
  }
  
  // Toggle ELA analysis mode
  toggleElaMode(event) {
    const isElaMode = event.target.checked
    
    if (isElaMode) {
      // Uncheck other modes
      if (this.hasCompareSwitchTarget) this.compareSwitchTarget.checked = false
      if (this.hasCopyMoveSwitchTarget) this.copyMoveSwitchTarget.checked = false
      if (this.hasDoctamperSwitchTarget) this.doctamperSwitchTarget.checked = false
      if (this.hasDoctamperHeatmapSwitchTarget) this.doctamperHeatmapSwitchTarget.checked = false
      if (this.hasPdfAnnotationsSwitchTarget) this.pdfAnnotationsSwitchTarget.checked = false
      
      // Uncheck and disable filter modes
      if (this.hasBlackWhiteSwitchTarget) {
        this.blackWhiteSwitchTarget.checked = false
        this.removeBlackWhiteFilter()
      }
      if (this.hasInvertColorSwitchTarget) {
        this.invertColorSwitchTarget.checked = false
        this.removeInvertColorFilter()
      }
      
      // Hide copy-move overlay if visible
      if (this.hasCopyMoveOverlayTarget) {
        this.copyMoveOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      
      // Hide Doctamper overlay if visible
      if (this.hasDoctamperOverlayTarget) {
        this.doctamperOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
          this.clearDoctamperBoundingBoxes(overlay)
        })
      }
      // Hide Doctamper Heatmap overlay if visible
      if (this.hasDoctamperHeatmapOverlayTarget) {
        this.doctamperHeatmapOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      
      // Show ELA overlay
      if (this.hasElaOverlayTarget) {
        this.elaOverlayTargets.forEach(overlay => {
          overlay.classList.remove('d-none')
        })
      }
      
      // Keep zoom buttons enabled
      // User requested zoom to work in all modes
    } else {
      // Hide ELA overlay
      if (this.hasElaOverlayTarget) {
        this.elaOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
    }
  }
  
  // Toggle Copy-Move detection mode
  toggleCopyMoveMode(event) {
    const isCopyMoveMode = event.target.checked
    
    if (isCopyMoveMode) {
      // Uncheck other modes
      if (this.hasCompareSwitchTarget) this.compareSwitchTarget.checked = false
      if (this.hasElaSwitchTarget) this.elaSwitchTarget.checked = false
      if (this.hasDoctamperSwitchTarget) this.doctamperSwitchTarget.checked = false
      if (this.hasDoctamperHeatmapSwitchTarget) this.doctamperHeatmapSwitchTarget.checked = false
      if (this.hasPdfAnnotationsSwitchTarget) this.pdfAnnotationsSwitchTarget.checked = false
      
      // Uncheck and disable filter modes
      if (this.hasBlackWhiteSwitchTarget) {
        this.blackWhiteSwitchTarget.checked = false
        this.removeBlackWhiteFilter()
      }
      if (this.hasInvertColorSwitchTarget) {
        this.invertColorSwitchTarget.checked = false
        this.removeInvertColorFilter()
      }
      
      // Hide ELA overlay if visible
      if (this.hasElaOverlayTarget) {
        this.elaOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      
      // Hide Doctamper overlay if visible
      if (this.hasDoctamperOverlayTarget) {
        this.doctamperOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
          this.clearDoctamperBoundingBoxes(overlay)
        })
      }
      // Hide Doctamper Heatmap overlay if visible
      if (this.hasDoctamperHeatmapOverlayTarget) {
        this.doctamperHeatmapOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      
      // Show Copy-Move overlay
      if (this.hasCopyMoveOverlayTarget) {
        this.copyMoveOverlayTargets.forEach(overlay => {
          overlay.classList.remove('d-none')
        })
      }
      
      // Keep zoom buttons enabled
      // User requested zoom to work in all modes
    } else {
      // Hide Copy-Move overlay
      if (this.hasCopyMoveOverlayTarget) {
        this.copyMoveOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
    }
  }
  
  // Toggle Doctamper detection mode
  toggleDoctamperMode(event) {
    const isDoctamperMode = event.target.checked
    
    if (isDoctamperMode) {
      // Uncheck other modes
      if (this.hasCompareSwitchTarget) this.compareSwitchTarget.checked = false
      if (this.hasElaSwitchTarget) this.elaSwitchTarget.checked = false
      if (this.hasCopyMoveSwitchTarget) this.copyMoveSwitchTarget.checked = false
      if (this.hasPdfAnnotationsSwitchTarget) this.pdfAnnotationsSwitchTarget.checked = false
      
      // Uncheck and disable filter modes
      if (this.hasBlackWhiteSwitchTarget) {
        this.blackWhiteSwitchTarget.checked = false
        this.removeBlackWhiteFilter()
      }
      if (this.hasInvertColorSwitchTarget) {
        this.invertColorSwitchTarget.checked = false
        this.removeInvertColorFilter()
      }
      
      // Hide other overlays
      if (this.hasElaOverlayTarget) {
        this.elaOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      if (this.hasCopyMoveOverlayTarget) {
        this.copyMoveOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      
      // Show Doctamper overlay and create bounding boxes
      if (this.hasDoctamperOverlayTarget) {
        this.doctamperOverlayTargets.forEach(overlay => {
          overlay.classList.remove('d-none')
          this.createDoctamperBoundingBoxes(overlay)
        })
      }
      
      // Apply grayscale to images when in bounding box mode
      this.applyDoctamperGrayscale()
    } else {
      // Hide Doctamper overlay and clear bounding boxes
      if (this.hasDoctamperOverlayTarget) {
        this.doctamperOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
          this.clearDoctamperBoundingBoxes(overlay)
        })
      }
      
      // Remove grayscale when exiting bounding box mode
      this.removeDoctamperGrayscale()
    }
  }
  
  // Toggle Doctamper Heatmap mode
  toggleDoctamperHeatmapMode(event) {
    const isDoctamperHeatmapMode = event.target.checked
    
    if (isDoctamperHeatmapMode) {
      // Uncheck other modes
      if (this.hasCompareSwitchTarget) this.compareSwitchTarget.checked = false
      if (this.hasElaSwitchTarget) this.elaSwitchTarget.checked = false
      if (this.hasCopyMoveSwitchTarget) this.copyMoveSwitchTarget.checked = false
      if (this.hasDoctamperSwitchTarget) this.doctamperSwitchTarget.checked = false
      if (this.hasPdfAnnotationsSwitchTarget) this.pdfAnnotationsSwitchTarget.checked = false
      
      // Uncheck and disable filter modes
      if (this.hasBlackWhiteSwitchTarget) {
        this.blackWhiteSwitchTarget.checked = false
        this.removeBlackWhiteFilter()
      }
      if (this.hasInvertColorSwitchTarget) {
        this.invertColorSwitchTarget.checked = false
        this.removeInvertColorFilter()
      }
      
      // Hide other overlays
      if (this.hasElaOverlayTarget) {
        this.elaOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      if (this.hasCopyMoveOverlayTarget) {
        this.copyMoveOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      if (this.hasDoctamperOverlayTarget) {
        this.doctamperOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
          this.clearDoctamperBoundingBoxes(overlay)
        })
      }
      if (this.hasPdfAnnotationsOverlayTarget) {
        this.pdfAnnotationsOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
          this.clearPdfAnnotationBoundingBoxes(overlay)
        })
      }
      
      // Show Doctamper Heatmap overlay
      if (this.hasDoctamperHeatmapOverlayTarget) {
        this.doctamperHeatmapOverlayTargets.forEach(overlay => {
          overlay.classList.remove('d-none')
        })
      }
    } else {
      // Hide Doctamper Heatmap overlay
      if (this.hasDoctamperHeatmapOverlayTarget) {
        this.doctamperHeatmapOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
    }
  }
  
  // Toggle PDF Annotations mode
  togglePdfAnnotationsMode(event) {
    const isPdfAnnotationsMode = event.target.checked
    
    if (isPdfAnnotationsMode) {
      // Uncheck other modes
      if (this.hasCompareSwitchTarget) this.compareSwitchTarget.checked = false
      if (this.hasElaSwitchTarget) this.elaSwitchTarget.checked = false
      if (this.hasCopyMoveSwitchTarget) this.copyMoveSwitchTarget.checked = false
      if (this.hasDoctamperSwitchTarget) this.doctamperSwitchTarget.checked = false
      if (this.hasDoctamperHeatmapSwitchTarget) this.doctamperHeatmapSwitchTarget.checked = false
      
      // Uncheck and disable filter modes
      if (this.hasBlackWhiteSwitchTarget) {
        this.blackWhiteSwitchTarget.checked = false
        this.removeBlackWhiteFilter()
      }
      if (this.hasInvertColorSwitchTarget) {
        this.invertColorSwitchTarget.checked = false
        this.removeInvertColorFilter()
      }
      
      // Hide other overlays
      if (this.hasElaOverlayTarget) {
        this.elaOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      if (this.hasCopyMoveOverlayTarget) {
        this.copyMoveOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      if (this.hasDoctamperOverlayTarget) {
        this.doctamperOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
          this.clearDoctamperBoundingBoxes(overlay)
        })
      }
      if (this.hasDoctamperHeatmapOverlayTarget) {
        this.doctamperHeatmapOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      
      // Show PDF Annotations overlay and create bounding boxes
      if (this.hasPdfAnnotationsOverlayTarget) {
        this.pdfAnnotationsOverlayTargets.forEach(overlay => {
          const overlayPageIndex = parseInt(overlay.dataset.pageIndex)
          if (overlayPageIndex === this.currentPage) {
            overlay.classList.remove('d-none')
            this.createPdfAnnotationBoundingBoxes(overlay)
          }
        })
      }
      
      // Apply black and white filter when in PDF annotations mode
      this.applyBlackWhiteFilter()
    } else {
      // Hide PDF Annotations overlay and clear bounding boxes
      if (this.hasPdfAnnotationsOverlayTarget) {
        this.pdfAnnotationsOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
          this.clearPdfAnnotationBoundingBoxes(overlay)
        })
      }
      
      // Hide tooltip when exiting PDF annotations mode
      this.hideAnnotationTooltip()
      
      // Remove black and white filter when exiting PDF annotations mode
      this.removeBlackWhiteFilter()
    }
  }
  
  // Toggle Black and White mode
  toggleBlackWhiteMode(event) {
    const isBlackWhiteMode = event.target.checked
    
    if (isBlackWhiteMode) {
      // Uncheck invert color mode if active
      if (this.hasInvertColorSwitchTarget) this.invertColorSwitchTarget.checked = false
      
      // Uncheck ELA and copy-move modes if active
      if (this.hasElaSwitchTarget) this.elaSwitchTarget.checked = false
      if (this.hasCopyMoveSwitchTarget) this.copyMoveSwitchTarget.checked = false
      
      // Hide overlays
      if (this.hasElaOverlayTarget) {
        this.elaOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      if (this.hasCopyMoveOverlayTarget) {
        this.copyMoveOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      
      // Remove invert filter first
      this.removeInvertColorFilter()
      
      // Apply grayscale filter to all document images
      this.applyBlackWhiteFilter()
    } else {
      // Remove grayscale filter
      this.removeBlackWhiteFilter()
    }
  }
  
  // Toggle Invert Color mode
  toggleInvertColorMode(event) {
    const isInvertColorMode = event.target.checked
    
    if (isInvertColorMode) {
      // Uncheck black and white mode if active
      if (this.hasBlackWhiteSwitchTarget) this.blackWhiteSwitchTarget.checked = false
      
      // Uncheck ELA and copy-move modes if active
      if (this.hasElaSwitchTarget) this.elaSwitchTarget.checked = false
      if (this.hasCopyMoveSwitchTarget) this.copyMoveSwitchTarget.checked = false
      
      // Hide overlays
      if (this.hasElaOverlayTarget) {
        this.elaOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      if (this.hasCopyMoveOverlayTarget) {
        this.copyMoveOverlayTargets.forEach(overlay => {
          overlay.classList.add('d-none')
        })
      }
      
      // Remove grayscale filter first
      this.removeBlackWhiteFilter()
      
      // Apply invert filter to all document images
      this.applyInvertColorFilter()
    } else {
      // Remove invert filter
      this.removeInvertColorFilter()
    }
  }
  
  // Helper method to apply black and white filter
  applyBlackWhiteFilter() {
    // Only apply to regular preview images, not comparison images
    const images = this.element.querySelectorAll('.document-app__preview-image, .document-app__compare-original img')
    images.forEach(img => {
      const currentFilter = img.style.filter || ''
      const nonGrayscaleFilters = currentFilter.replace(/grayscale\([^)]*\)/g, '').trim()
      img.style.filter = nonGrayscaleFilters ? `${nonGrayscaleFilters} grayscale(1)` : 'grayscale(1)'
    })
  }
  
  // Helper method to remove black and white filter
  removeBlackWhiteFilter() {
    // Only remove from regular preview images, not comparison images
    const images = this.element.querySelectorAll('.document-app__preview-image, .document-app__compare-original img')
    images.forEach(img => {
      const currentFilter = img.style.filter || ''
      const nonGrayscaleFilters = currentFilter.replace(/grayscale\([^)]*\)/g, '').trim()
      img.style.filter = nonGrayscaleFilters
    })
  }
  
  // Helper method to apply invert color filter
  applyInvertColorFilter() {
    // Only apply to regular preview images, not comparison images
    const images = this.element.querySelectorAll('.document-app__preview-image, .document-app__compare-original img')
    images.forEach(img => {
      const currentFilter = img.style.filter || ''
      const nonInvertFilters = currentFilter.replace(/invert\([^)]*\)/g, '').trim()
      img.style.filter = nonInvertFilters ? `${nonInvertFilters} invert(1)` : 'invert(1)'
    })
  }
  
  // Helper method to remove invert color filter
  removeInvertColorFilter() {
    // Only remove from regular preview images, not comparison images
    const images = this.element.querySelectorAll('.document-app__preview-image, .document-app__compare-original img')
    images.forEach(img => {
      const currentFilter = img.style.filter || ''
      const nonInvertFilters = currentFilter.replace(/invert\([^)]*\)/g, '').trim()
      img.style.filter = nonInvertFilters
    })
  }
  
  // Toggle JSON view mode
  toggleJsonMode(event) {
    const isJsonMode = event.target.checked
    
    if (isJsonMode) {
      // Hide preview container and show JSON view
      if (this.hasPreviewContainerTarget) this.previewContainerTarget.classList.add('d-none')
      if (this.hasJsonViewTarget) this.jsonViewTarget.classList.remove('d-none')
      
      // Show copy button
      if (this.hasCopyJsonButtonTarget) this.copyJsonButtonTarget.classList.remove('d-none')
      
      // Hide other controls (but keep download button)
      const sidebarButtons = this.element.querySelectorAll('.document-app__icon-btn')
      sidebarButtons.forEach(button => {
        const isJsonToggle = button.getAttribute('for') === 'jsonSwitch'
        const isCopyButton = button === this.copyJsonButtonTarget
        const isDownloadButton = button.getAttribute('data-action')?.includes('download-button#startDownload')
        
        if (!isJsonToggle && !isCopyButton && !isDownloadButton) {
          button.classList.add('d-none')
        }
      })
      
      // Don't hide download wrapper anymore
      // const downloadWrapper = this.element.querySelector('.document-app__download-wrapper')
      // if (downloadWrapper) downloadWrapper.classList.add('d-none')
      
      // Hide version dropdown if exists
      const versionDropdown = this.element.querySelector('.document-app__version-dropdown')
      if (versionDropdown) versionDropdown.classList.add('d-none')
      
      // Hide zoom section
      const zoomSection = this.element.querySelector('.document-app__zoom-section')
      if (zoomSection) zoomSection.classList.add('d-none')
      
      // Hide manipulation section if exists
      const manipulationSection = this.element.querySelector('.document-app__manipulation-section')
      if (manipulationSection) manipulationSection.classList.add('d-none')
      
      // Hide filter section if exists
      const filterSection = this.element.querySelector('.document-app__filter-section')
      if (filterSection) filterSection.classList.add('d-none')
      
      // Hide page navigation
      const pageNavigation = this.element.querySelector('.document-app__icon-sidebar-bottom')
      if (pageNavigation) pageNavigation.classList.add('d-none')
    } else {
      // Show preview container and hide JSON view
      if (this.hasPreviewContainerTarget) this.previewContainerTarget.classList.remove('d-none')
      if (this.hasJsonViewTarget) this.jsonViewTarget.classList.add('d-none')
      
      // Hide copy button
      if (this.hasCopyJsonButtonTarget) this.copyJsonButtonTarget.classList.add('d-none')
      
      // Show all hidden controls
      const sidebarButtons = this.element.querySelectorAll('.document-app__icon-btn.d-none')
      sidebarButtons.forEach(button => {
        const isCopyButton = button === this.copyJsonButtonTarget
        if (!isCopyButton) {
          button.classList.remove('d-none')
        }
      })
      
      // Download wrapper is always visible now, no need to show it
      // const downloadWrapper = this.element.querySelector('.document-app__download-wrapper')
      // if (downloadWrapper) downloadWrapper.classList.remove('d-none')
      
      // Show version dropdown if exists
      const versionDropdown = this.element.querySelector('.document-app__version-dropdown')
      if (versionDropdown) versionDropdown.classList.remove('d-none')
      
      // Show zoom section
      const zoomSection = this.element.querySelector('.document-app__zoom-section')
      if (zoomSection) zoomSection.classList.remove('d-none')
      
      // Show manipulation section if exists
      const manipulationSection = this.element.querySelector('.document-app__manipulation-section')
      if (manipulationSection) manipulationSection.classList.remove('d-none')
      
      // Show filter section if exists
      const filterSection = this.element.querySelector('.document-app__filter-section')
      if (filterSection) filterSection.classList.remove('d-none')
      
      // Show page navigation
      const pageNavigation = this.element.querySelector('.document-app__icon-sidebar-bottom')
      if (pageNavigation) pageNavigation.classList.remove('d-none')
    }
  }
  
  // Copy JSON to clipboard
  copyJsonToClipboard() {
    if (!this.hasJsonViewTarget) return
    
    // Get the text content from the JSON view
    const jsonText = this.jsonViewTarget.querySelector('code')?.textContent || this.jsonViewTarget.textContent
    
    // Copy to clipboard
    navigator.clipboard.writeText(jsonText).then(() => {
      // Show success feedback on the button
      const icon = this.copyJsonButtonTarget.querySelector('i')
      if (icon) {
        icon.classList.remove('fa-copy')
        icon.classList.add('fa-check')
        
        // Reset icon after 2 seconds
        setTimeout(() => {
          icon.classList.remove('fa-check')
          icon.classList.add('fa-copy')
        }, 2000)
      }
    }).catch(err => {
      console.error('Failed to copy JSON:', err)
    })
  }
  
  // Helper method to hide all views
  hideAllViews() {
    if (this.hasRegularViewTarget) this.regularViewTarget.classList.add('d-none')
    if (this.hasComparisonViewTarget) this.comparisonViewTarget.classList.add('d-none')
  }
  
  // Helper method to toggle zoom buttons
  toggleZoomButtons(disable) {
    const zoomButtons = this.element.querySelectorAll('.btn[data-action^="magnifier#"]')
    zoomButtons.forEach(button => {
      button.disabled = disable
      
      if (disable) {
        // Hide buttons completely in comparison mode to avoid any borders
        button.style.visibility = 'hidden'
        button.style.opacity = '0'
        button.style.pointerEvents = 'none'
        button.setAttribute('title', 'Zoom not available in comparison mode')
      } else {
        // Reset styles
        button.style.visibility = ''
        button.style.opacity = ''
        button.style.pointerEvents = ''
        
        // Restore original titles
        if (button.getAttribute('data-action') === 'magnifier#zoomIn') {
          button.setAttribute('title', 'Zoom in')
        } else if (button.getAttribute('data-action') === 'magnifier#zoomOut') {
          button.setAttribute('title', 'Zoom out')
        } else if (button.getAttribute('data-action') === 'magnifier#resetZoom') {
          button.setAttribute('title', 'Reset zoom')
        }
      }
    })
  }
  
  // Helper method to update comparison pages
  updateComparisonPages() {
    if (this.hasComparisonPageTarget) {
      this.comparisonPageTargets.forEach((page, index) => {
        const pageIndex = parseInt(page.dataset.pageIndex)
        if (pageIndex === this.currentPage) {
          page.classList.remove('d-none')
          page.classList.add('active')
        } else {
          page.classList.add('d-none')
          page.classList.remove('active')
        }
      })
    }
  }
  
  // Select document version
  selectVersion(event) {
    const selectedVersion = event.target.value
    
    // Hide all version images first
    if (this.hasVersionImageTarget) {
      this.versionImageTargets.forEach(img => {
        img.style.display = 'none'
      })
      
      // Show images for the selected version
      this.versionImageTargets.forEach(img => {
        if (img.dataset.version === selectedVersion) {
          img.style.display = 'block'
        }
      })
    }
  }
  
  // Select version from dropdown (sidebar implementation)
  selectVersionFromDropdown(event) {
    event.preventDefault()
    const selectedVersion = event.currentTarget.dataset.version
    
    // Update the hidden select if it exists
    if (this.hasVersionSelectorTarget) {
      this.versionSelectorTarget.value = selectedVersion
      // Trigger change event on the select to run selectVersion logic
      this.versionSelectorTarget.dispatchEvent(new Event('change'))
    }
    
    // Close the dropdown
    const dropdown = event.currentTarget.closest('.dropdown-menu')
    if (dropdown && window.bootstrap) {
      const dropdownInstance = bootstrap.Dropdown.getInstance(dropdown.previousElementSibling)
      if (dropdownInstance) {
        dropdownInstance.hide()
      }
    }
  }
  
  // Slider comparison functionality
  startDrag(event) {
    this.isDragging = true
    
    // Store the slider element
    this.currentSlider = event.currentTarget.closest('[data-document-pages-target="compareSlider"]')
    
    // Prevent default to avoid text selection during drag
    event.preventDefault()
  }
  
  handleDrag(event) {
    if (!this.isDragging || !this.currentSlider) return
    
    // Get slider dimensions
    const sliderRect = this.currentSlider.getBoundingClientRect()
    
    // Calculate position (mouse or touch)
    let clientX = 0
    if (event.type === 'touchmove') {
      clientX = event.touches[0].clientX
    } else {
      clientX = event.clientX
    }
    
    // Calculate percentage (constrained between 0-100%)
    let percentage = ((clientX - sliderRect.left) / sliderRect.width) * 100
    percentage = Math.max(0, Math.min(100, percentage))
    
    // Find the relevant slider handle and version container for this slider
    const handle = this.currentSlider.querySelector('[data-document-pages-target="sliderHandle"]')
    const versionContainer = this.currentSlider.querySelector('[data-document-pages-target="compareVersionContainer"]')
    const versionImage = versionContainer ? versionContainer.querySelector('img') : null
    const originalContainer = this.currentSlider.querySelector('.document-app__compare-original')
    const originalImage = originalContainer ? originalContainer.querySelector('img') : null
    
    if (handle && versionContainer) {
      // Update slider position
      handle.style.left = `${percentage}%`
      versionContainer.style.width = `${percentage}%`
      
      // Important: Ensure proper sizing for both images
      const containerWidth = this.currentSlider.offsetWidth
      
      // Maintain original image sizing
      if (originalImage) {
        originalImage.style.position = 'absolute'
        originalImage.style.top = '0'
        originalImage.style.left = '0'
        originalImage.style.width = '100%'
        originalImage.style.height = '100%'
        originalImage.style.objectFit = 'contain'
        originalImage.style.objectPosition = 'center'
      }
      
      // Ensure version image doesn't resize when slider moves
      if (versionImage) {
        versionImage.style.position = 'absolute'
        versionImage.style.top = '0'
        versionImage.style.left = '0'
        versionImage.style.width = `${containerWidth}px`
        versionImage.style.height = '100%'
        versionImage.style.maxWidth = 'none'
        versionImage.style.objectFit = 'contain'
        versionImage.style.objectPosition = 'center'
        versionImage.style.transform = 'none'
      }
    }
  }
  
  stopDrag() {
    this.isDragging = false
    this.currentSlider = null
  }

  updatePageDisplay() {
    // Handle page counter if it exists
    if (this.hasCurrentPageNumTarget) {
      this.currentPageNumTarget.textContent = this.currentPage + 1
    }
    
    // Check if this document has pagination controls (PDF) or is a single image
    const hasPagination = this.hasPrevButtonTarget && this.hasNextButtonTarget
    
    // Only update button states if pagination exists
    if (hasPagination) {
      this.prevButtonTarget.disabled = this.currentPage === 0 || this.totalPages <= 1
      this.nextButtonTarget.disabled = this.currentPage === this.totalPages - 1 || this.totalPages <= 1
    }
    
    // Check if we're in comparison mode
    const isCompareMode = this.hasCompareSwitchTarget && this.compareSwitchTarget.checked
    
    // Update regular view (if not in compare mode or if we need to keep it updated in the background)
    if (!isCompareMode || true) { // Always update regular view for proper state tracking
      // Update page positions with 3D effect
      this.pageTargets.forEach((page, index) => {
        const pageIndex = parseInt(page.dataset.pageIndex)
        
        // Calculate relative position to current page
        const relativePos = pageIndex - this.currentPage
        
        // Apply 3D transformations
        if (relativePos === 0) {
          // Current page - front and center
          page.style.transform = `translateZ(0px) translateY(0px) rotateY(0deg)`
          page.style.opacity = "1"
          page.style.zIndex = "100"
          page.classList.add("active")
        } else if (relativePos > 0) {
          // Pages after current - stack to the right
          page.style.transform = `translateZ(${-15 * relativePos}px) translateY(${7 * relativePos}px) rotateY(${-2 * relativePos}deg)`
          page.style.opacity = `${Math.max(0.6, 1 - (0.15 * relativePos))}`
          page.style.zIndex = `${100 - relativePos}`
          page.classList.remove("active")
        } else {
          // Pages before current - stack to the left
          const absPos = Math.abs(relativePos)
          page.style.transform = `translateZ(${-15 * absPos}px) translateY(${7 * absPos}px) rotateY(${2 * absPos}deg)`
          page.style.opacity = `${Math.max(0.6, 1 - (0.15 * absPos))}`
          page.style.zIndex = `${100 - absPos}`
          page.classList.remove("active")
        }
      })
    }
    
    // Update comparison pages if in compare mode
    if (this.hasCompareSwitchTarget && this.compareSwitchTarget.checked) {
      this.updateComparisonPages()
    }
    
    // Update indicators if they exist
    if (this.hasIndicatorTarget) {
      this.indicatorTargets.forEach((indicator, index) => {
        if (index === this.currentPage) {
          indicator.style.width = "30px"
          indicator.style.backgroundColor = "#4a87ff"
        } else {
          indicator.style.width = "8px"
          indicator.style.backgroundColor = "#d9e1ee"
        }
      })
    }
    
    // Update PDF annotations if they're currently shown
    if (this.hasPdfAnnotationsSwitchTarget && this.pdfAnnotationsSwitchTarget.checked) {
      this.pdfAnnotationsOverlayTargets.forEach(overlay => {
        const overlayPageIndex = parseInt(overlay.dataset.pageIndex)
        if (overlayPageIndex === this.currentPage) {
          overlay.classList.remove('d-none')
          this.createPdfAnnotationBoundingBoxes(overlay)
        } else {
          overlay.classList.add('d-none')
        }
      })
    }
  }
  
  // Create Doctamper bounding boxes
  createDoctamperBoundingBoxes(overlayElement) {
    // Clear any existing boxes first
    this.clearDoctamperBoundingBoxes(overlayElement)
    
    // Parse the image manipulation data
    if (!this.hasImageManipulationValue || !this.imageManipulationValue) return
    
    try {
      const data = JSON.parse(this.imageManipulationValue)
      const boundingBoxes = data?.detections?.doctamper?.bounding_boxes
      const metadata = data?.metadata
      
      if (!boundingBoxes || !Array.isArray(boundingBoxes)) return
      
      // Get the image element
      const imageWrapper = overlayElement.parentElement
      const image = imageWrapper.querySelector('img')
      if (!image) return
      
      // Wait for image to load if not already loaded
      if (!image.complete) {
        image.addEventListener('load', () => this.createDoctamperBoundingBoxes(overlayElement))
        return
      }
      
      // Get original image dimensions from metadata
      const originalWidth = metadata?.original_size?.[0] || image.naturalWidth
      const originalHeight = metadata?.original_size?.[1] || image.naturalHeight
      
      // Create SVG overlay with viewBox matching original image dimensions
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
      svg.style.position = 'absolute'
      svg.style.top = '0'
      svg.style.left = '0'
      svg.style.width = '100%'
      svg.style.height = '100%'
      svg.style.pointerEvents = 'none'
      svg.setAttribute('viewBox', `0 0 ${originalWidth} ${originalHeight}`)
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet')
      svg.classList.add('doctamper-svg-overlay')
      
      // Create bounding boxes using original coordinates
      boundingBoxes.forEach((box, index) => {
        // Create a group for box and text
        const group = document.createElementNS("http://www.w3.org/2000/svg", "g")
        group.style.cursor = 'pointer'
        
        // Create rectangle with original coordinates
        const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
        rect.setAttribute('x', box.x)
        rect.setAttribute('y', box.y)
        rect.setAttribute('width', box.width)
        rect.setAttribute('height', box.height)
        rect.setAttribute('fill', this.getConfidenceColor(box.max_confidence))
        rect.setAttribute('fill-opacity', '0.3')
        rect.setAttribute('stroke', this.getConfidenceColor(box.max_confidence))
        rect.setAttribute('stroke-width', '3')
        rect.classList.add('doctamper-box')
        
        // Add confidence label (hidden by default)
        const text = document.createElementNS("http://www.w3.org/2000/svg", "text")
        text.setAttribute('x', box.x + box.width / 2)
        text.setAttribute('y', box.y + box.height / 2)
        text.setAttribute('text-anchor', 'middle')
        text.setAttribute('dominant-baseline', 'middle')
        text.setAttribute('fill', 'white')
        text.setAttribute('font-size', '24')
        text.setAttribute('font-weight', 'bold')
        text.style.textShadow = '2px 2px 4px rgba(0,0,0,0.8)'
        text.style.display = 'none'
        text.textContent = `${Math.round(box.max_confidence * 100)}%`
        
        // Add hover events
        group.addEventListener('mouseenter', () => {
          rect.setAttribute('fill-opacity', '0.5')
          text.style.display = 'block'
        })
        
        group.addEventListener('mouseleave', () => {
          rect.setAttribute('fill-opacity', '0.3')
          text.style.display = 'none'
        })
        
        group.appendChild(rect)
        group.appendChild(text)
        svg.appendChild(group)
      })
      
      overlayElement.appendChild(svg)
    } catch (error) {
      console.error('Error creating doctamper bounding boxes:', error)
    }
  }
  
  // Clear Doctamper bounding boxes
  clearDoctamperBoundingBoxes(overlayElement) {
    const svg = overlayElement.querySelector('.doctamper-svg-overlay')
    if (svg) {
      svg.remove()
    }
  }
  
  // Get confidence-based color
  getConfidenceColor(confidence) {
    if (confidence >= 0.7) return '#ff0000' // High confidence - red
    if (confidence >= 0.4) return '#ff8800' // Medium confidence - orange
    return '#ffaa00' // Low confidence - yellow
  }
  
  // Apply grayscale filter when in doctamper bounding box mode
  applyDoctamperGrayscale() {
    const images = this.element.querySelectorAll('.document-app__preview-image, .document-app__compare-original img')
    images.forEach(img => {
      const currentFilter = img.style.filter || ''
      const nonGrayscaleFilters = currentFilter.replace(/grayscale\([^)]*\)/g, '').trim()
      img.style.filter = nonGrayscaleFilters ? `${nonGrayscaleFilters} grayscale(1)` : 'grayscale(1)'
    })
  }
  
  // Remove grayscale filter when exiting doctamper bounding box mode
  removeDoctamperGrayscale() {
    const images = this.element.querySelectorAll('.document-app__preview-image, .document-app__compare-original img')
    images.forEach(img => {
      const currentFilter = img.style.filter || ''
      const nonGrayscaleFilters = currentFilter.replace(/grayscale\([^)]*\)/g, '').trim()
      img.style.filter = nonGrayscaleFilters
    })
  }
  
  // Create PDF annotation bounding boxes
  createPdfAnnotationBoundingBoxes(overlayElement) {
    // Clear any existing boxes first
    this.clearPdfAnnotationBoundingBoxes(overlayElement)
    
    // Parse the resurrect data
    if (!this.hasResurrectDataValue || !this.resurrectDataValue) return
    
    try {
      const data = JSON.parse(this.resurrectDataValue)
      // Use filtered annotations that exclude hidden edits
      const annotations = data?.pdf_annotations?.annotations_for_display || data?.pdf_annotations?.annotations
      
      if (!annotations || !Array.isArray(annotations)) return
      
      // Get the page index from the overlay element
      const overlayPageIndex = parseInt(overlayElement.dataset.pageIndex) || 0
      const pageNumber = overlayPageIndex + 1 // PDF pages are 1-indexed
      
      // Filter annotations for this specific page
      const pageAnnotations = annotations.filter(ann => ann.page === pageNumber)
      
      if (pageAnnotations.length === 0) return
      
      // Get the image element
      const imageWrapper = overlayElement.parentElement
      const image = imageWrapper.querySelector('img')
      if (!image) return
      
      // Wait for image to load if not already loaded
      if (!image.complete) {
        image.addEventListener('load', () => this.createPdfAnnotationBoundingBoxes(overlayElement))
        return
      }
      
      // Create container for boxes and tooltips
      const container = document.createElement('div')
      container.style.position = 'absolute'
      container.style.top = '0'
      container.style.left = '0'
      container.style.width = '100%'
      container.style.height = '100%'
      container.style.pointerEvents = 'auto'
      container.style.zIndex = '200'
      container.classList.add('pdf-annotations-container')
      
      // Process annotations individually
      pageAnnotations.forEach((annotation, index) => {
        // Skip if no bbox
        if (!annotation.bbox || annotation.bbox.length !== 4) return
        
        // Skip OverlappingText (hidden edits) - these should not be displayed
        if (annotation.type === 'OverlappingText' || annotation.change_type === 'hidden_edit') return
        
        // Create annotation group
        const annotationGroup = document.createElement('div')
        annotationGroup.style.position = 'absolute'
        
        // Check if this is an image comparison annotation (pixel coordinates)
        if (annotation.Type === 'ImageComparison') {
          // Use pixel coordinates directly for image comparison
          const [x1, y1, x2, y2] = annotation.bbox
          const boxX = Math.min(x1, x2)
          const boxY = Math.min(y1, y2)
          const boxWidth = Math.abs(x2 - x1)
          const boxHeight = Math.abs(y2 - y1)
          
          // Get actual image dimensions and position
          const containerRect = overlayElement.getBoundingClientRect()
          const imageRect = image.getBoundingClientRect()
          const imageActualWidth = image.naturalWidth
          const imageActualHeight = image.naturalHeight
          
          // Calculate the offset of the image within its container
          const imageOffsetX = imageRect.left - containerRect.left
          const imageOffsetY = imageRect.top - containerRect.top
          
          // Calculate scale between displayed image and actual image
          const scaleX = imageRect.width / imageActualWidth
          const scaleY = imageRect.height / imageActualHeight
          
          // Apply pixel-based positioning scaled to current display size, accounting for image offset
          annotationGroup.style.left = `${imageOffsetX + (boxX * scaleX)}px`
          annotationGroup.style.top = `${imageOffsetY + (boxY * scaleY)}px`
          annotationGroup.style.width = `${boxWidth * scaleX}px`
          annotationGroup.style.height = `${boxHeight * scaleY}px`
        } else {
          // Use PDF coordinates for regular annotations
          const [x1, y1, x2, y2] = annotation.bbox
          const boxX = Math.min(x1, x2)
          const boxY = Math.min(y1, y2)
          const boxWidth = Math.abs(x2 - x1)
          const boxHeight = Math.abs(y2 - y1)
          
          // Get page dimensions from annotation or use defaults
          const pageWidth = annotation.page_width || 595   // Default to A4 width if not specified
          const pageHeight = annotation.page_height || 842  // Default to A4 height if not specified
          
          // Apply percentage-based positioning for PDF annotations
          annotationGroup.style.left = `${(boxX / pageWidth) * 100}%`
          annotationGroup.style.top = `${(boxY / pageHeight) * 100}%`
          annotationGroup.style.width = `${(boxWidth / pageWidth) * 100}%`
          annotationGroup.style.height = `${(boxHeight / pageHeight) * 100}%`
        }
        annotationGroup.style.cursor = 'pointer'
        annotationGroup.classList.add('pdf-annotation-group')
        
        // Create bounding box
        const box = document.createElement('div')
        box.style.position = 'absolute'
        box.style.top = '0'
        box.style.left = '0'
        box.style.width = '100%'
        box.style.height = '100%'
        box.style.border = '3px solid'
        box.style.borderRadius = '4px'
        box.style.transition = 'all 0.3s ease'
        
        // Set color based on annotation type
        let color = '#0066FF' // Default blue
        if (annotation.Type === 'ImageComparison') {
          color = '#FF0000' // Red for image comparison differences
        } else if (annotation.type === 'FontMixing') {
          color = '#FF0000' // Red for font mixing manipulations
        } else if (annotation.type === 'FreeText' && annotation.change_type === 'modification') {
          color = '#FF8800' // Orange for modifications
        } else if (annotation.type === 'OverlappingText' && annotation.change_type === 'hidden_edit') {
          color = '#FF0000' // Red for hidden edits
        }
        
        box.style.borderColor = color
        box.style.backgroundColor = color
        box.style.opacity = '0.2'
        
        // Create alert icon for modified text annotations only
        if (annotation.change_type === 'modification' || annotation.Type === 'ImageComparison') {
          const iconContainer = document.createElement('div')
          iconContainer.style.position = 'absolute'
          iconContainer.style.left = '-40px' // Position to the left of the box
          iconContainer.style.top = '50%'
          iconContainer.style.transform = 'translateY(-50%)'
          iconContainer.style.width = '30px'
          iconContainer.style.height = '30px'
          iconContainer.style.pointerEvents = 'none'
          iconContainer.innerHTML = `
            <svg viewBox="0 0 234.409 234.409" style="width: 100%; height: 100%; fill: #FF0000;">
              <g>
                <path d="M117.204,30.677c-47.711,0-86.527,38.816-86.527,86.528c0,47.711,38.816,86.526,86.527,86.526s86.527-38.815,86.527-86.526
                  C203.732,69.494,164.915,30.677,117.204,30.677z M117.204,188.732c-39.44,0-71.527-32.086-71.527-71.526
                  c0-39.441,32.087-71.528,71.527-71.528s71.527,32.087,71.527,71.528C188.732,156.645,156.645,188.732,117.204,188.732z"></path>
                <path d="M44.896,44.897c2.929-2.929,2.929-7.678,0-10.607c-2.93-2.929-7.678-2.929-10.607,0
                  c-45.718,45.719-45.718,120.111,0,165.831c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.197
                  c2.93-2.929,2.93-7.677,0.001-10.606C5.026,149.643,5.026,84.768,44.896,44.897z"></path>
                <path d="M200.119,34.29c-2.93-2.929-7.678-2.929-10.607,0c-2.929,2.929-2.929,7.678,0,10.607
                  c39.872,39.871,39.872,104.746,0,144.618c-2.929,2.929-2.929,7.678,0,10.606c1.465,1.464,3.385,2.197,5.304,2.197
                  c1.919,0,3.839-0.732,5.304-2.197C245.839,154.4,245.839,80.009,200.119,34.29z"></path>
                <path d="M117.204,140.207c4.143,0,7.5-3.358,7.5-7.5v-63.88c0-4.142-3.357-7.5-7.5-7.5c-4.143,0-7.5,3.358-7.5,7.5v63.88
                  C109.704,136.849,113.062,140.207,117.204,140.207z"></path>
                <circle cx="117.204" cy="156.254" r="9.329"></circle>
              </g>
            </svg>
          `
          annotationGroup.appendChild(iconContainer)
        }
        
        // Store annotation data for tooltip
        annotationGroup.dataset.tooltipContent = this.buildAnnotationTooltip(annotation, color)
        annotationGroup.dataset.annotationColor = color
        
        // Add hover events for shared tooltip
        annotationGroup.addEventListener('mouseenter', (e) => {
          box.style.opacity = '0.4'
          box.style.transform = 'scale(1.02)'
          this.showAnnotationTooltip(e.currentTarget)
        })
        
        annotationGroup.addEventListener('mouseleave', () => {
          box.style.opacity = '0.2'
          box.style.transform = 'scale(1)'
          this.hideAnnotationTooltip()
        })
        
        annotationGroup.appendChild(box)
        container.appendChild(annotationGroup)
      })
      
      overlayElement.appendChild(container)
    } catch (error) {
      console.error('Error creating PDF annotation bounding boxes:', error)
    }
  }
  
  // Clear PDF annotation bounding boxes
  clearPdfAnnotationBoundingBoxes(overlayElement) {
    const container = overlayElement.querySelector('.pdf-annotations-container')
    if (container) {
      container.remove()
    }
  }
  
  // Get annotation type badge text
  getAnnotationTypeBadge(type, changeType) {
    if (type === 'ImageComparison') return 'Visual Difference'
    if (changeType === 'hidden_edit') return 'Hidden Edit'
    if (changeType === 'modification') return 'Modified Text'
    if (type === 'Square') return 'Highlight'
    if (type === 'FreeText') return 'Text Edit'
    return type || 'Edit'
  }
  
  // Escape HTML for safe display
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
  
  // Create shared tooltip element
  createSharedTooltip() {
    const tooltip = document.createElement('div')
    tooltip.style.position = 'absolute'
    tooltip.style.backgroundColor = 'rgba(0, 0, 0, 0.95)'
    tooltip.style.color = 'white'
    tooltip.style.padding = '16px 20px'
    tooltip.style.borderRadius = '12px'
    tooltip.style.fontSize = '14px'
    tooltip.style.display = 'none'
    tooltip.style.backdropFilter = 'blur(10px)'
    tooltip.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)'
    tooltip.style.minWidth = '280px'
    tooltip.style.maxWidth = '400px'
    tooltip.style.pointerEvents = 'none'
    tooltip.style.transition = 'opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
    return tooltip
  }
  
  // Group annotations by overlapping bounding boxes
  groupOverlappingAnnotations(annotations) {
    const groups = []
    const processed = new Set()
    
    annotations.forEach((ann, index) => {
      if (processed.has(index) || !ann.bbox) return
      
      const group = [ann]
      processed.add(index)
      
      // Find all annotations that should be grouped with this one
      annotations.forEach((other, otherIndex) => {
        if (processed.has(otherIndex) || !other.bbox || index === otherIndex) return
        
        // Check if this annotation should be added to the group
        let shouldGroup = false
        
        // Check if it overlaps with ANY annotation already in the group
        for (const groupMember of group) {
          if (this.boundingBoxesOverlap(groupMember.bbox, other.bbox, 0.5)) {
            shouldGroup = true
            break
          }
        }
        
        // Also group if they're close together and have similar modification times
        if (!shouldGroup && Math.abs(index - otherIndex) <= 3) {
          // Check if modification dates are close (within 2 minutes)
          if (ann.modification_date && other.modification_date) {
            const date1 = new Date(ann.modification_date).getTime()
            const date2 = new Date(other.modification_date).getTime()
            if (Math.abs(date1 - date2) < 120000) { // 2 minutes
              shouldGroup = true
            }
          }
          
          // Or if they're very close spatially
          if (!shouldGroup && this.boundingBoxesNearby(ann.bbox, other.bbox, 20)) {
            shouldGroup = true
          }
        }
        
        if (shouldGroup) {
          group.push(other)
          processed.add(otherIndex)
        }
      })
      
      groups.push(group)
    })
    
    return groups
  }
  
  // Check if two bounding boxes overlap by a certain percentage
  boundingBoxesOverlap(bbox1, bbox2, threshold = 0.7) {
    const [x1_1, y1_1, x2_1, y2_1] = bbox1
    const [x1_2, y1_2, x2_2, y2_2] = bbox2
    
    // Calculate intersection
    const xLeft = Math.max(x1_1, x1_2)
    const yTop = Math.max(y1_1, y1_2)
    const xRight = Math.min(x2_1, x2_2)
    const yBottom = Math.min(y2_1, y2_2)
    
    if (xLeft > xRight || yTop > yBottom) return false
    
    const intersectionArea = (xRight - xLeft) * (yBottom - yTop)
    const area1 = (x2_1 - x1_1) * (y2_1 - y1_1)
    const area2 = (x2_2 - x1_2) * (y2_2 - y1_2)
    
    // Calculate overlap percentage (intersection over smaller area)
    const overlapPercentage = intersectionArea / Math.min(area1, area2)
    
    return overlapPercentage >= threshold
  }
  
  // Check if two bounding boxes are nearby (within a certain distance)
  boundingBoxesNearby(bbox1, bbox2, maxDistance = 20) {
    const [x1_1, y1_1, x2_1, y2_1] = bbox1
    const [x1_2, y1_2, x2_2, y2_2] = bbox2
    
    // Calculate center points
    const center1X = (x1_1 + x2_1) / 2
    const center1Y = (y1_1 + y2_1) / 2
    const center2X = (x1_2 + x2_2) / 2
    const center2Y = (y1_2 + y2_2) / 2
    
    // Calculate distance between centers
    const distance = Math.sqrt(
      Math.pow(center2X - center1X, 2) + 
      Math.pow(center2Y - center1Y, 2)
    )
    
    return distance <= maxDistance
  }
  
  // Get merged bounding box for a group of annotations
  getMergedBoundingBox(group) {
    if (!group.length) return null
    
    let minX = Infinity, minY = Infinity
    let maxX = -Infinity, maxY = -Infinity
    
    group.forEach(ann => {
      if (!ann.bbox) return
      const [x1, y1, x2, y2] = ann.bbox
      minX = Math.min(minX, x1, x2)
      minY = Math.min(minY, y1, y2)
      maxX = Math.max(maxX, x1, x2)
      maxY = Math.max(maxY, y1, y2)
    })
    
    return [minX, minY, maxX, maxY]
  }
  
  // Get color based on the most specific annotation type in the group
  getGroupColor(group) {
    // Priority: hidden edit > modification > highlight
    const hasHiddenEdit = group.some(ann => 
      ann.type === 'OverlappingText' && ann.change_type === 'hidden_edit'
    )
    if (hasHiddenEdit) return '#FF0000' // Red
    
    const hasModification = group.some(ann => 
      ann.type === 'FreeText' && ann.change_type === 'modification'
    )
    if (hasModification) return '#FF8800' // Orange
    
    return '#0066FF' // Default blue
  }
  
  // Build tooltip content for a single annotation
  buildAnnotationTooltip(annotation, color) {
    let html = '<div style="display: flex; flex-direction: column; gap: 12px;">'
    
    // Type badge - handle both 'type' and 'Type' fields
    const type = annotation.type || annotation.Type
    const typeBadge = this.getAnnotationTypeBadge(type, annotation.change_type)
    html += `<div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
      <span style="background-color: ${color}; color: white; padding: 4px 12px; border-radius: 6px; font-size: 12px; font-weight: 600;">
        ${typeBadge}
      </span>
    </div>`
    
    // For image comparison, show the note
    if (type === 'ImageComparison' && annotation.note) {
      html += `<div>
        <div style="color: #999; font-size: 12px; margin-bottom: 4px;">Detection:</div>
        <div style="background-color: rgba(255, 255, 255, 0.1); padding: 8px 12px; border-radius: 6px;">
          ${this.escapeHtml(annotation.note)}
        </div>
      </div>`
    } else {
      // Original text (if available)
      if (annotation.original_text) {
        html += `<div>
          <div style="color: #999; font-size: 12px; margin-bottom: 4px;">Original Text:</div>
          <div style="background-color: rgba(255, 255, 255, 0.1); padding: 8px 12px; border-radius: 6px; font-family: monospace;">
            ${this.escapeHtml(annotation.original_text)}
          </div>
        </div>`
      }
      
      // New text (if available)
      if (annotation.new_text || annotation.content) {
        html += `<div>
          <div style="color: #999; font-size: 12px; margin-bottom: 4px;">Changed To:</div>
          <div style="background-color: rgba(255, 255, 255, 0.1); padding: 8px 12px; border-radius: 6px; font-family: monospace;">
            ${this.escapeHtml(annotation.new_text || annotation.content)}
          </div>
        </div>`
      }
    }
    
    // Overlapping text (if available)
    if (annotation.overlapping_text && annotation.overlapping_text !== annotation.new_text) {
      html += `<div>
        <div style="color: #999; font-size: 12px; margin-bottom: 4px;">Overlapping Text:</div>
        <div style="background-color: rgba(255, 255, 255, 0.1); padding: 8px 12px; border-radius: 6px; font-family: monospace;">
          ${this.escapeHtml(annotation.overlapping_text)}
        </div>
      </div>`
    }
    
    // Metadata
    html += '<div style="display: flex; flex-direction: column; gap: 6px; margin-top: 8px; padding-top: 12px; border-top: 1px solid rgba(255, 255, 255, 0.1);">'
    
    if (annotation.author) {
      html += `<div style="display: flex; align-items: center; gap: 8px;">
        <i class="fa-solid fa-user" style="color: #999; width: 16px;"></i>
        <span style="color: #ccc;">${this.escapeHtml(annotation.author)}</span>
      </div>`
    }
    
    if (annotation.modification_date) {
      const date = new Date(annotation.modification_date)
      const formattedDate = date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
      html += `<div style="display: flex; align-items: center; gap: 8px;">
        <i class="fa-solid fa-clock" style="color: #999; width: 16px;"></i>
        <span style="color: #ccc;">${formattedDate}</span>
      </div>`
    }
    
    if (annotation.overlap_percentage) {
      html += `<div style="display: flex; align-items: center; gap: 8px;">
        <i class="fa-solid fa-percent" style="color: #999; width: 16px;"></i>
        <span style="color: #ccc;">Overlap: ${annotation.overlap_percentage.toFixed(1)}%</span>
      </div>`
    }
    
    if (annotation.detection_method) {
      html += `<div style="display: flex; align-items: center; gap: 8px;">
        <i class="fa-solid fa-magnifying-glass" style="color: #999; width: 16px;"></i>
        <span style="color: #ccc;">Detection: ${annotation.detection_method}</span>
      </div>`
    }
    
    html += '</div></div>'
    
    return html
  }
  
  // Show annotation tooltip at correct position
  showAnnotationTooltip(annotationElement) {
    if (!this.sharedTooltip || !this.hasTooltipOverlayTarget) return
    
    // Get annotation position relative to viewport
    const rect = annotationElement.getBoundingClientRect()
    const overlayRect = this.tooltipOverlayTarget.getBoundingClientRect()
    
    // Update tooltip content
    this.sharedTooltip.innerHTML = annotationElement.dataset.tooltipContent
    
    // Calculate initial position relative to overlay
    const left = rect.left + rect.width / 2 - overlayRect.left
    const top = rect.top - overlayRect.top
    
    // Position tooltip above the annotation initially
    this.sharedTooltip.style.left = `${left}px`
    this.sharedTooltip.style.top = `${top - 12}px` // 12px gap above annotation
    this.sharedTooltip.style.transform = 'translateX(-50%) translateY(-100%)'
    
    // Show tooltip temporarily to get its dimensions
    this.sharedTooltip.style.display = 'block'
    this.sharedTooltip.style.opacity = '0'
    
    // Check boundaries and adjust if necessary
    requestAnimationFrame(() => {
      const tooltipRect = this.sharedTooltip.getBoundingClientRect()
      
      // Check if tooltip goes off the top
      if (tooltipRect.top < overlayRect.top) {
        // Position below the annotation instead
        this.sharedTooltip.style.top = `${rect.bottom - overlayRect.top + 12}px`
        this.sharedTooltip.style.transform = 'translateX(-50%)'
      }
      
      // Check horizontal boundaries
      if (tooltipRect.left < overlayRect.left) {
        // Adjust to stay within left boundary
        this.sharedTooltip.style.left = `${tooltipRect.width / 2 + 10}px`
      } else if (tooltipRect.right > overlayRect.right) {
        // Adjust to stay within right boundary
        this.sharedTooltip.style.left = `${overlayRect.width - tooltipRect.width / 2 - 10}px`
      }
      
      // Animate in
      this.sharedTooltip.style.opacity = '1'
    })
  }
  
  // Hide annotation tooltip
  hideAnnotationTooltip() {
    if (!this.sharedTooltip) return
    
    this.sharedTooltip.style.opacity = '0'
    
    // Hide after transition
    setTimeout(() => {
      if (this.sharedTooltip.style.opacity === '0') {
        this.sharedTooltip.style.display = 'none'
      }
    }, 300)
  }
}